import React, { useEffect, useState, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import { SEOHead } from '@/components/seo';
import { Header, Footer, useGuideRequest } from '@/components/public';
import { ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DOMAIN_FILTERS = [
  { key: '',            label: 'All' },
  { key: 'voice',       label: 'Voice' },
  { key: 'chatbots',    label: 'Chatbots' },
  { key: 'hiring',      label: 'Hiring' },
  { key: 'kids',        label: 'Kids' },
  { key: 'health',      label: 'Health' },
  { key: 'fintech',     label: 'Fintech' },
  { key: 'image-video', label: 'Image & video' },
  { key: 'agents',      label: 'Agents' },
  { key: 'frontier',    label: 'Frontier' },
];

const formatDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return ''; }
};

const LawWatchIndexPage = () => {
  const location = useLocation();
  const { open: openGuide } = useGuideRequest();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeDomain, setActiveDomain] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await axios.get(`${API}/law-watch`, {
          params: { limit: 50, ...(activeDomain ? { domain: activeDomain } : {}) },
        });
        if (!cancelled) setItems(res.data.items || []);
      } catch (e) {
        if (!cancelled) setError('Could not load articles right now.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeDomain]);

  const jsonLd = useMemo(() => JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    'name': 'AI Law Watch',
    'url': `https://starteaseai.com${location.pathname}`,
    'description': 'US AI laws explained for founders — what each new law changes for the people building.',
    'author': { '@type': 'Person', 'name': 'Santosh Singh' },
    'blogPost': items.map((it) => ({
      '@type': 'BlogPosting',
      'headline': it.title,
      'url': `https://starteaseai.com/ai-law-watch/${it.slug}`,
      'datePublished': it.published_at,
      'dateModified': it.verified_at || it.published_at,
    })),
  }), [items, location.pathname]);

  return (
    <div className="min-h-screen bg-[#0b1310] text-white" data-testid="law-watch-index">
      <SEOHead
        title="AI Law Watch — What each new US AI law means for your startup | StartEase Agent"
        description="A founder-facing register of US AI laws. Voice, chatbots, hiring, kids, health, fintech — what each statute actually changes for the people building."
        canonical="https://starteaseai.com/ai-law-watch"
      />
      {/* eslint-disable-next-line react/no-danger */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLd }} />

      <Header />

      <div className="max-w-[760px] mx-auto px-7">
        <header className="pt-16 pb-2">
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold leading-[1.1] mb-4">AI Law Watch</h1>
          <p className="text-slate-400 text-base leading-[1.7] max-w-[640px]">
            Every few weeks a US state passes another law that reaches AI products — usually without
            the founders it affects hearing about it. I read the statutes, the law-firm alerts, and
            the court dockets, and write down what each one actually changes for the people building.
            No legal jargon. Real deadlines, real cases, real product decisions.{' '}
            <span className="italic text-white">— Santosh Singh</span>
          </p>
          <p className="text-slate-500 text-[13px] mt-4">
            74 laws on watch · 84 startup domains · updated as the law moves
          </p>
        </header>

        <div className="pt-7 pb-1.5 border-b border-white/10 text-[13.5px] flex flex-wrap gap-x-4 gap-y-2" data-testid="law-watch-filters">
          {DOMAIN_FILTERS.map((f) => {
            const on = activeDomain === f.key;
            return (
              <button
                key={f.key || 'all'}
                onClick={() => setActiveDomain(f.key)}
                className={`pb-2 transition-colors ${on ? 'text-white border-b-2 border-[#059669]' : 'text-slate-400 hover:text-white'}`}
                data-testid={`law-watch-filter-${f.key || 'all'}`}
              >
                {f.label}
              </button>
            );
          })}
        </div>

        {loading && (
          <p className="py-10 text-slate-500 text-sm" data-testid="law-watch-loading">Loading articles…</p>
        )}
        {error && !loading && (
          <p className="py-10 text-slate-500 text-sm" data-testid="law-watch-error">{error}</p>
        )}
        {!loading && !error && items.length === 0 && (
          <p className="py-10 text-slate-500 text-sm" data-testid="law-watch-empty">
            No articles yet in this category.
          </p>
        )}

        {items.map((it) => (
          <Link
            key={it.slug}
            to={`/ai-law-watch/${it.slug}`}
            className="block py-7 border-b border-white/5 group"
            data-testid={`law-watch-entry-${it.slug}`}
          >
            <div className="text-slate-500 text-[13px] mb-2">
              <span className="text-[#e8a13c] font-medium">{it.jurisdiction}</span>
              <span> · {formatDate(it.published_at)}</span>
            </div>
            <h2 className="font-serif text-2xl font-semibold leading-[1.28] mb-2 text-white group-hover:text-[#059669] transition-colors">
              {it.title}
            </h2>
            <p className="text-slate-400 text-[15px] leading-[1.65] max-w-[640px]">{it.hook}</p>
            <div className="text-slate-500 text-[12.5px] mt-2.5">
              {(it.domains || []).join(' · ')}
              {it.verified_at && <span> — checked against sources {formatDate(it.verified_at)}</span>}
            </div>
          </Link>
        ))}

        <div
          className="my-12 mb-20 rounded-[10px] p-7 border border-white/10 bg-[#101b14]"
          data-testid="law-watch-risk-cta"
        >
          <p className="text-slate-400 text-[15px] leading-[1.7]">
            <span className="text-white font-semibold">
              Building in one of these categories and not sure which laws reach you?
            </span>{' '}
            Send me one message about what you&apos;re building and I&apos;ll name the laws you&apos;re
            inside — which documents they demand, and what covers you. Free, usually within a day
            or two.{' '}
            <button
              type="button"
              onClick={() => openGuide('law-watch:index')}
              className="text-[#059669] underline underline-offset-[3px] bg-transparent border-0 p-0 cursor-pointer"
              data-testid="law-watch-risk-link"
            >
              Start here.
            </button>
          </p>
          <p className="italic mt-2 text-[13.5px] text-slate-500">— Santosh, StartEase Agent</p>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default LawWatchIndexPage;
